
import React, { useState, useMemo, useEffect } from 'react';
import { MATERIALS } from '../engine/pipeDatabase';
import {
  calculateVelocity,
  calculateReynoldsNumber,
  calculateDarcyFrictionFactor,
} from '../engine/hydraulics';
import { getWaterProperties } from '../engine/fluids';
import { useLanguage } from '../i18n/LanguageContext';
import { getCalculatorToolText } from '../i18n/calculatorToolTranslations';
import { PressureDropReportData } from './PrintableReport';

interface PressureDropCalculatorProps {
  onPrint?: () => void;
  onReportDataChange?: (data: PressureDropReportData | null) => void;
}

export const PressureDropCalculator: React.FC<PressureDropCalculatorProps> = ({
  onPrint,
  onReportDataChange,
}) => {
  const { language } = useLanguage();
  const t = getCalculatorToolText(language);

  const [materialId, setMaterialId] = useState<string>('carbon_steel_sch40');
  const [rawDiameter, setRawDiameter] = useState<string>('52.5');
  const [rawLength, setRawLength] = useState<string>('60');
  const [rawFlow, setRawFlow] = useState<string>('3.5');
  const [elbowCount] = useState<number>(4);

  const dMm = parseFloat(rawDiameter);
  const lengthM = parseFloat(rawLength);
  const flowLps = parseFloat(rawFlow);

  const validationError = useMemo(() => {
    if (isNaN(dMm) || dMm <= 0) return t.invalidID;
    if (isNaN(lengthM) || lengthM <= 0) return t.invalidLength;
    if (isNaN(flowLps) || flowLps <= 0) return t.invalidFlow;
    if (!MATERIALS[materialId]) return t.invalidMaterial;
    return null;
  }, [dMm, lengthM, flowLps, materialId, t]);

  const result = useMemo(() => {
    if (validationError) return null;

    const flowM3s = flowLps * 0.001;
    const v = calculateVelocity(flowM3s, dMm);
    const fluid = getWaterProperties(20);
    const Re = calculateReynoldsNumber(v, dMm, fluid.kinematicViscosity);
    const roughness = MATERIALS[materialId]?.roughnessMm || 0.045;
    const f = calculateDarcyFrictionFactor(Re, roughness, dMm);

    const dM = dMm / 1000;
    const g = 9.80665;
    const frictionHeadM = f * (lengthM / dM) * (Math.pow(v, 2) / (2 * g));
    const minorHeadM = elbowCount * 0.75 * (Math.pow(v, 2) / (2 * g));
    const totalHeadM = frictionHeadM + minorHeadM;

    const dpPa = fluid.density * g * totalHeadM;
    const dpKPa = dpPa / 1000;
    const dpBar = dpPa / 100000;
    const dpPsi = dpPa * 0.000145038;
    const gradient = (dpKPa / lengthM) * 100;

    return {
      v,
      Re,
      f,
      frictionHeadM,
      totalHeadM,
      dpKPa,
      dpBar,
      dpPsi,
      gradient,
    };
  }, [dMm, lengthM, flowLps, materialId, elbowCount, validationError]);

  useEffect(() => {
    if (!onReportDataChange) return;

    if (validationError || !result) {
      onReportDataChange(null);
      return;
    }

    const selectedMaterial = MATERIALS[materialId];
    onReportDataChange({
      materialId,
      materialName: selectedMaterial?.name ?? materialId,
      roughnessMm: selectedMaterial?.roughnessMm ?? 0.045,
      rawDiameter,
      rawLength,
      rawFlow,
      elbowCount,
      results: {
        velocityMs: result.v,
        reynoldsNumber: result.Re,
        frictionFactor: result.f,
        frictionHeadM: result.frictionHeadM,
        minorHeadM: Math.max(0, result.totalHeadM - result.frictionHeadM),
        totalHeadM: result.totalHeadM,
        dpKPa: result.dpKPa,
        dpBar: result.dpBar,
        dpPsi: result.dpPsi,
        gradientKPaPer100m: result.gradient,
      },
    });
  }, [
    validationError,
    result,
    materialId,
    rawDiameter,
    rawLength,
    rawFlow,
    elbowCount,
    onReportDataChange,
  ]);

  return (
    <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
      <div className="max-w-3xl">
        <span className="text-xs font-bold uppercase tracking-wider text-[#087F9C] block mb-1">
          {t.pressureBadge}
        </span>

        <h2 className="font-display font-extrabold text-2xl text-[#10283D]">
          {t.pressureTitle}
        </h2>

        <p className="text-sm text-slate-600 mt-1">
          {t.pressureDescription}
        </p>
      </div>

      {validationError && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-semibold">
          ⚠️ {validationError}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 pt-4 border-t border-slate-100">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.pipeMaterial}
          </label>

          <select
            value={materialId}
            onChange={(e) => setMaterialId(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-medium bg-white"
          >
            {Object.values(MATERIALS).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.pipeID}
          </label>

          <input
            type="number"
            step="0.5"
            value={rawDiameter}
            onChange={(e) => setRawDiameter(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.runLength}
          </label>

          <input
            type="number"
            step="1"
            value={rawLength}
            onChange={(e) => setRawLength(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.flowRate}
          </label>

          <input
            type="number"
            step="0.1"
            value={rawFlow}
            onChange={(e) => setRawFlow(e.target.value)}
            className="w-full p-2.5 rounded-xl border border-slate-300 text-sm font-semibold"
          />
        </div>
      </div>

      {result && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-100">
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
              {t.totalPressureDrop}
            </span>

            <span className="font-display font-extrabold text-2xl text-[#10283D]">
              {result.dpKPa.toFixed(1)} kPa
            </span>

            <span className="block text-xs text-slate-500 mt-1">
              {result.dpBar.toFixed(3)} bar / {result.dpPsi.toFixed(2)} psi
            </span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
              {t.totalHeadLoss}
            </span>

            <span className="font-display font-extrabold text-2xl text-[#087F9C]">
              {result.totalHeadM.toFixed(2)} m
            </span>

            <span className="block text-xs text-slate-500 mt-1">
              {(result.totalHeadM * 3.28084).toFixed(2)} ft of water
            </span>
          </div>

          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block mb-1">
              {t.hydraulicGradient}
            </span>

            <span className="font-display font-extrabold text-2xl text-slate-800">
              {result.gradient.toFixed(1)} kPa/100m
            </span>

            <span className="block text-xs text-emerald-600 mt-1 font-semibold">
              {t.frictionFactor} = {result.f.toFixed(4)}
            </span>
          </div>
        </div>
      )}

      {onPrint && result && (
        <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-slate-500 font-medium">
            Generate verified Darcy-Weisbach pressure drop &amp; friction analysis report
          </span>
          <button
            type="button"
            onClick={onPrint}
            disabled={!!validationError}
            className="py-2.5 px-5 rounded-2xl font-bold text-xs bg-[#10283D] hover:bg-[#1E3A5F] active:bg-[#087F9C] text-white shadow-md flex items-center gap-2 transition-all duration-200 border border-slate-700/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="w-4 h-4 text-[#39BFD3]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            <span>PDF Report / Print Analysis</span>
          </button>
        </div>
      )}
    </div>
  );
};
